import type { ActivitySource, ActivityViewData, ShowModalFn } from "../types";
import { ActivityUIKind } from "../constants";
import type { DatabaseAdapter } from "@rapid/providers/database/DatabaseAdapter";
import { convertAsyncTask } from "./asyncTaskMappings";
import type { FirestoreAsyncTask } from "./types";

/**
 * ActivitySource backed by the `async_tasks` Firestore collection.
 *
 * Listens to top-level tasks (parentTaskId == null) created in the last 7 days
 * for a given workspace, converts each Firestore document into an ActivityViewData,
 * and pushes updates in real-time via onSnapshot.
 *
 * Only tasks registered in taskMappings are surfaced.
 * Unregistered task types are silently filtered out.
 */
class AsyncTaskSource implements ActivitySource {
    private workspaceId: string;
    private database: DatabaseAdapter;
    private showModal: ShowModalFn;

    constructor(workspaceId: string, database: DatabaseAdapter, showModal: ShowModalFn) {
        this.workspaceId = workspaceId;
        this.database = database;
        this.showModal = showModal;
    }

    subscribe(callback: (activities: ActivityViewData[]) => void): () => void {
        if (!this.workspaceId) {
            callback([]);
            return () => {};
        }

        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        return this.database.onQuerySnapshot(
            "async_tasks",
            {
                where: [
                    ["owner", "==", this.workspaceId],
                    ["parentTaskId", "==", null],
                    ["createdAt", ">=", sevenDaysAgo]
                ],
                orderBy: ["createdAt", "desc"],
                limit: 50
            },
            (docs) => {
                const activities: ActivityViewData[] = [];
                for (const doc of docs) {
                    const task = { ...doc.data(), id: doc.id } as FirestoreAsyncTask;
                    const converted = convertAsyncTask(task, this.showModal);
                    if (converted) activities.push(converted);
                }
                callback(activities);
            },
            (error) => {
                // eslint-disable-next-line no-console
                console.error("AsyncTaskSource onSnapshot error:", error);
                callback([]);
            }
        );
    }
}

export { AsyncTaskSource };
